/**
 * Menüwahl im Builder (lesend, sql/771, docs/KONZEPT-PAKET-MENUEWAHL.md Ablauf 2): ein Kasten „Menüwahl · 3 Menüs · Gast verteilt
 * 80 Gäste“ über den Positionen einer Variante, deren Paket mit „Gast wählt zwischen diesen Menüs“ übernommen wurde. Er zeigt
 * die Menüs mit Art und Aufpreis je Gast; geändert wird am Paket, nicht hier. Ein einzelnes Menü ist keine Wahl (kein Kasten).
 * Meldungen des Servers zur Menüwahl (422) stehen ruhig daneben (`MenuewahlMeldung`), nicht als roter Fehlerkasten.
 */
import { Card, Icon } from "./components";
import { money } from "./format";
import type { OfferItem } from "./types";

export const MENUE_ART_TEXT: Record<string, string> = { vegan: "vegan", vegetarisch: "vegetarisch", fisch_fleisch: "Fisch/Fleisch" };

export interface MenuewahlGruppe {
  gruppe: string;
  gaeste: number;
  menues: { id: string; name: string; art: string | null; aufpreisCents: number }[];
}

/** Die Menüwahlen einer Variante: Kopfzeilen mit gleicher Kennung, mindestens zwei, nicht optional. `gaeste` je Menü-Kopfzeile. */
export function menuewahlGruppen(items: readonly OfferItem[], gaesteJeZeile: (it: OfferItem) => number): MenuewahlGruppe[] {
  const gruppen = new Map<string, OfferItem[]>();
  for (const it of items) {
    if (!it.menueWahlGruppe || it.isOptional) continue;
    gruppen.set(it.menueWahlGruppe, [...(gruppen.get(it.menueWahlGruppe) ?? []), it]);
  }
  return [...gruppen.entries()]
    .filter(([, k]) => k.length >= 2)
    .map(([gruppe, k]) => {
      // Gäste der Paketzeile (gleiches Paket, gleicher Tag, keine Menü-Herkunft); sonst die der ersten Kopfzeile.
      const paket = items.find((it) => it.packageId != null && it.packageId === k[0]!.packageId && !it.menueWahlGruppe && !it.paketMenueId && !it.isOptional && (it.dayNo ?? null) === (k[0]!.dayNo ?? null));
      return {
        gruppe,
        gaeste: gaesteJeZeile(paket ?? k[0]!),
        menues: k.map((it) => ({ id: it.id, name: it.name, art: it.menueArt ?? null, aufpreisCents: it.unitPriceCents })),
      };
    });
}

export function MenuewahlKasten({ gruppen }: { gruppen: MenuewahlGruppe[] }) {
  if (gruppen.length === 0) return null;
  return (
    <div className="grid gap-3" data-testid="menuewahl-kasten">
      {gruppen.map((g) => (
        <Card key={g.gruppe} className="p-4" data-testid={`menuewahl-kasten-${g.gruppe}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Icon name="restaurant_menu" className="text-[18px] text-primary" />
            <h3 className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim" data-testid="menuewahl-kasten-titel">
              Menüwahl · {g.menues.length} Menüs · Gast verteilt {g.gaeste === 1 ? "1 Gast" : `${g.gaeste} Gäste`}
            </h3>
          </div>
          <ul className="mt-2 grid gap-1.5">
            {g.menues.map((m) => (
              <li key={m.id} className="flex flex-wrap items-baseline justify-between gap-x-3 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface" data-testid={`menuewahl-kasten-menue-${m.id}`}>
                <span className="min-w-0 break-words font-semibold">
                  {m.name}
                  {m.art ? <span className="ml-2 rounded-full bg-surface-variant px-2 py-0.5 text-[11px] font-semibold dark:bg-surface-variant/20">{MENUE_ART_TEXT[m.art] ?? m.art}</span> : null}
                </span>
                <span className="text-secondary dark:text-secondary-fixed-dim">{m.aufpreisCents > 0 ? `+ ${money(m.aufpreisCents)} pro Gast` : "ohne Aufpreis"}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">Der Paketpreis bleibt gleich, nur Menüs mit Aufpreis ändern den Betrag. Die Menüs und ihre Preise ändern Sie am Paket.</p>
        </Card>
      ))}
    </div>
  );
}

/** Die Server-Meldungen zur Menüwahl (422 aus dem Speichern und dem Paket-Übernehmen) - erkannt am Wortlaut der API. */
const MENUEWAHL_MELDUNG = /Menüwahl|Gast wählt zwischen diesen Menüs|Menü der Menüwahl|Hauptmenü für einen Teil der Gäste/;
export function istMenuewahlMeldung(text: string | null | undefined): boolean {
  return !!text && MENUEWAHL_MELDUNG.test(text);
}

/** Ruhige Darstellung einer Menüwahl-Meldung: ein Hinweis, kein Fehlerkasten. */
export function MenuewahlMeldung({ text }: { text: string }) {
  return (
    <p className="flex items-start gap-2 rounded-[10px] bg-[#F7ECD3] px-3 py-2.5 font-body-sm text-[13px] text-[#6F4A0A] dark:bg-[#8A5A0B]/25 dark:text-[#E3B65E]" role="status" data-testid="menuewahl-meldung">
      <Icon name="info" className="mt-0.5 shrink-0 text-[17px]" />
      <span>{text}</span>
    </p>
  );
}
